import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { teamMembers, teamDepartments } from '@/mocks/teamMembers';

const generateLeadershipSchema = () => {
  const persons = teamMembers.map((member) => {
    const nameParts = member.name.split(' ');
    return {
      "@type": "Person",
      "name": member.name,
      "givenName": nameParts[0],
      "familyName": nameParts.slice(1).join(' '),
      "jobTitle": member.role,
      "description": member.bio,
      "url": member.linkedIn !== '#' ? member.linkedIn : undefined,
      "worksFor": {
        "@type": "Organization",
        "name": "Thoughtic Solutions",
        "url": "https://thoughtic.com"
      },
      "workLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": member.location
        }
      }
    };
  });

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Thoughtic Solutions Leadership Team",
    "numberOfItems": teamMembers.length,
    "itemListElement": persons.map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": p
    }))
  });
};

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Leadership Hero ── */
function LeadershipHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-40 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20professional%20background%20with%20subtle%20geometric%20shapes%20and%20leadership%20silhouettes%2C%20deep%20navy%20blue%20with%20warm%20gold%20amber%20accent%20lighting%2C%20corporate%20team%20collaboration%20aesthetic%2C%20soft%20bokeh%20and%20gradient%20lighting%20effects%2C%20minimalist%20composition%20suitable%20for%20executive%20team%20page%20hero%20banner%20with%20white%20text%20overlay%2C%20premium%20and%20authoritative%20feel&width=1600&height=900&seq=leadership-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-team-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Our People</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Meet the Minds
            <span className="text-primary-400"> Behind Thoughtic</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            A leadership team with deep technical roots, operational discipline, and an obsession with delivering measurable client outcomes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 flex flex-wrap justify-center gap-3"
          >
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-global-line mr-1.5"></i>
              4 Countries
            </span>
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-user-star-line mr-1.5"></i>
              8 Leaders
            </span>
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-briefcase-line mr-1.5"></i>
              80+ Combined Years Experience
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '2', label: 'Co-Founders', icon: 'ri-lightbulb-line' },
    { value: '8', label: 'Leadership Team', icon: 'ri-team-line' },
    { value: '4', label: 'Countries', icon: 'ri-map-pin-line' },
    { value: '500+', label: 'Engineers Placed', icon: 'ri-user-search-line' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 mx-auto mb-2">
                <i className={`${stat.icon} text-primary-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Team Member Card ── */
function TeamMemberCard({ member, index }: { member: (typeof teamMembers)[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, ...springTransition }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-navy-900/5 transition-all duration-500 overflow-hidden"
    >
      <div className="relative h-72 overflow-hidden">
        <img
          src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28member.imageQuery%29%7D&width=600&height=700&seq=team-${member.id}&orientation=portrait`}
          alt={member.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-lg md:text-xl font-bold text-white mb-0.5">{member.name}</h3>
          <p className="text-xs text-white/80 font-medium">{member.role}</p>
        </div>
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/20 backdrop-blur-md rounded-lg text-[11px] font-medium text-white border border-white/20">
            <i className="ri-map-pin-line"></i>
            {member.location}
          </span>
        </div>
      </div>
      <div className="p-5 md:p-6">
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
          {member.bio}
        </p>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {expanded ? 'Show Less' : 'Read Full Bio'}
            <i className={expanded ? ' ri-arrow-up-s-line ml-1' : ' ri-arrow-down-s-line ml-1'}></i>
          </button>
          <a
            href={member.linkedIn}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 border border-primary-200 text-primary-600 hover:bg-primary-100 transition-colors"
            aria-label={`${member.name} LinkedIn`}
          >
            <i className="ri-linkedin-fill text-sm"></i>
          </a>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-4 mt-4 border-t border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {member.bio}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ── Leadership Grid ── */
function LeadershipGrid() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.02 });
  const [activeDept, setActiveDept] = useState('all');

  const filtered = activeDept === 'all'
    ? teamMembers
    : teamMembers.filter((m) => m.department === activeDept);

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-user-search-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Leadership</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 mb-4"
          >
            The People Building
            <span className="text-primary-600"> Your Teams</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Every leader at Thoughtic has shipped production systems at scale. They do not just manage talent — they understand the code, the architecture, and the pressure of a production outage.
          </motion.p>
        </motion.div>

        {/* Department Filter */}
        <motion.div style={{display:'none'}}
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="flex flex-wrap justify-center gap-2 mb-10 md:mb-14"
        >
          {teamDepartments.map((dept) => (
            <button
              key={dept.key}
              onClick={() => setActiveDept(dept.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeDept === dept.key
                  ? 'bg-navy-900 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {dept.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                activeDept === dept.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {dept.key === 'all' ? teamMembers.length : teamMembers.filter((m) => m.department === dept.key).length}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Team Grid */}
        <AnimatePresence mode="wait">
          <motion.div style={{display:'none'}}
            key={activeDept}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6"
          >
            {filtered.map((member, i) => (
              <TeamMemberCard key={member.id} member={member} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

/* ── Culture Section ── */
function CultureSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const values = [
    {
      icon: 'ri-code-box-line',
      title: 'Engineering-First',
      desc: 'Every leader at Thoughtic has written production code. We make technical decisions with technical people, not PowerPoint.',
    },
    {
      icon: 'ri-time-line',
      title: '48-Hour SLA',
      desc: 'We do not do "we will get back to you." From first contact to first candidate submission is 48 hours — guaranteed.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Domain Expertise',
      desc: 'A fintech engagement gets engineers who understand settlement cycles. Healthtech gets HIPAA-native builders. Domain fluency is non-negotiable.',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Outcome Obsession',
      desc: 'We measure ourselves by your metrics: ship velocity, system uptime, cost reduction. If we cannot measure it, we do not do it.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-heart-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Culture</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 mb-4"
          >
            How We
            <span className="text-primary-600"> Operate</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Our culture is not aspirational — it is operational. These four principles drive every decision we make.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary-500/20">
                <i className={`${v.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{v.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA Section ── */
function LeadershipCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-white-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm">
            <i className="ri-user-heart-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-navy/80">Ready to Connect?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy leading-tight mb-4 md:mb-6">
            Talk to the Team
            <span className="text-primary-400"> Directly</span>
          </h2>
          <p className="text-base md:text-lg text-gray/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Our leadership team is accessible. When you engage with Thoughtic, you are not handed off to a junior account manager — you work with people who understand your technical challenges.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Find Your Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/case-studies"
              className="px-8 py-4 border-2 border-gray/30 text-gray text-base font-semibold rounded-xl hover:bg-gray/10 hover:border-gray/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See Our Results
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
export default function LeadershipPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateLeadershipSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout title="Leadership" description="Meet the leadership team behind Thoughtic Solutions — technical founders and executives with 80+ combined years of experience.">
      <LeadershipHero />
      <LeadershipGrid />
      <StatsBar />
      <CultureSection />
      <LeadershipCTA />
    </PageLayout>
  );
}